import type { SubscriptionOfferCF, SubscriptionProductCF } from '@/types';
import { getOfferMembership, getOfferProducts } from './subscriptionOffer';

export interface OrderLine {
  id: string;
  name: string;
  price: string;
}

export interface OrderProductLine extends OrderLine {
  image?: SubscriptionProductCF['fields']['image'];
}

interface OrderBreakdown {
  lines: OrderLine[];
  total: string;
}

export interface OrderSummary {
  products: OrderProductLine[];
  monthly?: OrderBreakdown;
  annual: OrderBreakdown;
}

const MOCK_MEMBERSHIP_MONTHLY_PRICE_USD = 7.99;
const MOCK_ANNUAL_PRICES_USD: Record<string, number> = {
  Inspect: 499,
  Shine: 99,
};
const DEFAULT_MOCK_ANNUAL_PRICE_USD = 499;

const getMockAnnualPrice = (name: string) =>
  MOCK_ANNUAL_PRICES_USD[name] ?? DEFAULT_MOCK_ANNUAL_PRICE_USD;

// Placeholder pricing until the order summary comes from the subscriptions API.
// A bundle contributes one line per member product. The membership line and
// monthly breakdown only exist when the offer depends on a membership.
export function getMockOrderSummary(offer: SubscriptionOfferCF): OrderSummary {
  const products = getOfferProducts(offer).map(
    ({ sys: { id }, fields: { name, image } }) => ({
      id,
      name,
      image,
      annualPrice: getMockAnnualPrice(name),
    })
  );
  const annualTotal = products.reduce(
    (total, { annualPrice }) => total + annualPrice,
    0
  );

  const membership = getOfferMembership(offer);
  const membershipPrice = `$${MOCK_MEMBERSHIP_MONTHLY_PRICE_USD}`;

  return {
    products: [
      ...products.map(({ id, name, image, annualPrice }) => ({
        id,
        name: `Sunrun ${name}`,
        price: `$${annualPrice}/year`,
        image,
      })),
      ...(membership
        ? [
            {
              id: membership.sys.id,
              name: membership.fields.name,
              price: `${membershipPrice}/month`,
              image: membership.fields.image,
            },
          ]
        : []),
    ],
    monthly: membership && {
      lines: [
        {
          id: membership.sys.id,
          name: membership.fields.name,
          price: `${membershipPrice}/mo`,
        },
      ],
      total: `${membershipPrice}/mo`,
    },
    annual: {
      lines: products.map(({ id, name, annualPrice }) => ({
        id,
        name,
        price: `$${annualPrice}/yr`,
      })),
      total: `$${annualTotal}/yr`,
    },
  };
}
